'use client';

/** One month of days, drawn the same way wherever the panel asks for a date.
 *  Monday first, today ringed, days after `max` shown but not pickable. Local
 *  `YYYY-MM-DD` throughout: a UTC boundary moves this morning into yesterday. */

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const;

export function isoDay(at: Date): string {
  const pad = (one: number) => String(one).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

/** A local date from `YYYY-MM-DD`, or null for anything else. */
export function dayOf(value: string | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** "23 Sep 2026" or "Wed, 23 Sep 2026". Year always shown, a bare day reads
 *  ambiguous once records span years. Our own names, not the browser's: one ICU
 *  writes "Sept", another "Sep". */
export function dayLabel(value: string, withWeekday = false): string {
  const at = dayOf(value);
  if (!at) return value;
  const date = `${at.getDate()} ${MONTHS[at.getMonth()]} ${at.getFullYear()}`;
  return withWeekday ? `${DAYS[at.getDay()]}, ${date}` : date;
}

/** The first of the month a day falls in. */
export function monthOf(value: string | null | undefined): Date {
  const at = dayOf(value) ?? new Date();
  return new Date(at.getFullYear(), at.getMonth(), 1);
}

function shiftMonth(month: Date, by: number): Date {
  return new Date(month.getFullYear(), month.getMonth() + by, 1);
}

/** Six rows of seven, Monday first, spilling into the months either side. */
function weeksOf(month: Date): Date[][] {
  const back = (month.getDay() + 6) % 7;
  const start = new Date(month.getFullYear(), month.getMonth(), 1 - back);
  return Array.from({ length: 6 }, (_, week) =>
    Array.from({ length: 7 }, (_, day) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + week * 7 + day)),
  );
}

export function MonthGrid({
  month,
  onMonth,
  from,
  to,
  max,
  onPick,
}: {
  /** Any day in the month on screen; only its year and month are read. */
  month: Date;
  onMonth: (next: Date) => void;
  /** The chosen day, or the two ends of a range. Equal for a single day. */
  from?: string;
  to?: string;
  /** The last day that can be picked; later ones are drawn, but quiet. */
  max?: string;
  onPick: (day: string) => void;
}) {
  const today = isoDay(new Date());
  const title = month.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const lastAllowed = max ?? '';
  const canForward = !lastAllowed || isoDay(shiftMonth(month, 1)) <= lastAllowed;

  return (
    <div className="mg">
      <div className="mg-head">
        <button type="button" className="mg-step" aria-label="Previous month" onClick={() => onMonth(shiftMonth(month, -1))}>
          <Arrow way="back" />
        </button>
        <span className="mg-title">{title}</span>
        <button
          type="button"
          className="mg-step"
          aria-label="Next month"
          disabled={!canForward}
          onClick={() => onMonth(shiftMonth(month, 1))}
        >
          <Arrow way="on" />
        </button>
      </div>
      <div className="mg-grid" role="grid" aria-label={title}>
        {WEEKDAYS.map((day) => (
          <span key={day} className="mg-wd" role="columnheader">
            {day}
          </span>
        ))}
        {weeksOf(month).flat().map((at) => {
          const day = isoDay(at);
          const outside = at.getMonth() !== month.getMonth();
          const late = Boolean(lastAllowed) && day > lastAllowed;
          const edge = day === from || day === to;
          const inside = Boolean(from && to) && day > (from as string) && day < (to as string);
          const classes = [
            'mg-day',
            outside && 'is-outside',
            inside && 'is-between',
            edge && 'is-edge',
            day === today && 'is-today',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              key={day}
              type="button"
              className={classes}
              disabled={late}
              aria-pressed={edge}
              aria-label={at.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
              onClick={() => onPick(day)}
            >
              {at.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function Arrow({ way }: { way: 'back' | 'on' }) {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
      <path
        d={way === 'back' ? 'M10 4L6 8L10 12' : 'M6 4L10 8L6 12'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The calendar mark on a date control's face: drawn, not an emoji, so it
 *  takes the control's own colour in both themes. */
export function CalendarMark() {
  return (
    <svg className="mg-mark" width="14" height="14" viewBox="0 0 16 16" aria-hidden>
      <rect x="2" y="3.2" width="12" height="10.6" rx="1.6" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 6.4h12M5.2 1.8v2.6M10.8 1.8v2.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
